import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = window.location.origin;

const getEmbedUrl = (url, muted) => {
  if (!url) return null;
  const muteParam = muted ? 1 : 0;
  // YouTube
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?rel=0&autoplay=1&mute=${muteParam}&enablejsapi=1&loop=1&playlist=${ytMatch[1]}`;
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&muted=${muteParam}&loop=1`;
  // Google Drive
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (driveMatch) return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
  // Direct embed
  if (url.includes('embed') || url.includes('player')) return url;
  return url;
};

const isYouTube = (url) => /(?:youtube\.com|youtu\.be)/.test(url);
const isVimeo = (url) => /vimeo\.com/.test(url);

const WelcomeSection = () => {
  const { t } = useLanguage();
  const [videoUrl, setVideoUrl] = useState('');
  const [muted, setMuted] = useState(true);
  const iframeRef = useRef(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/settings/welcome_video`)
      .then(r => r.json())
      .then(d => { if (d.value) setVideoUrl(d.value); })
      .catch(() => {});
  }, []);

  const toggleMute = useCallback(() => {
    const newMuted = !muted;
    setMuted(newMuted);
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      if (isYouTube(videoUrl)) {
        iframe.contentWindow.postMessage(JSON.stringify({
          event: 'command',
          func: newMuted ? 'mute' : 'unMute'
        }), '*');
      } else if (isVimeo(videoUrl)) {
        iframe.contentWindow.postMessage(JSON.stringify({
          method: 'setVolume',
          value: newMuted ? 0 : 1
        }), '*');
      }
    } catch {}
  }, [muted, videoUrl]);

  if (!videoUrl) return null;

  const embedUrl = getEmbedUrl(videoUrl, muted);

  return (
    <section className="py-16" style={{ background: 'linear-gradient(180deg, #4d2a5e 0%, #3d1f5c 100%)' }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">{t('welcomeTitle')}</h2>
          <p className="text-gray-400">{t('welcomeDesc')}</p>
        </div>
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden aspect-video">
            <iframe
              ref={iframeRef}
              src={embedUrl}
              title="Welcome Video"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              frameBorder="0"
            />
            {/* Mute/Unmute Button */}
            <button
              onClick={toggleMute}
              className="absolute bottom-4 right-4 z-10 w-12 h-12 rounded-full bg-black/60 backdrop-blur-sm border border-white/20 flex items-center justify-center transition-all hover:bg-black/80 hover:scale-110"
              data-testid="welcome-video-mute-btn"
            >
              {muted ? (
                <VolumeX className="w-5 h-5 text-white" />
              ) : (
                <Volume2 className="w-5 h-5 text-white" />
              )}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WelcomeSection;
